import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/auth/login',
      name: 'Login',
      component: () => import('@/pages/auth/Login.vue'),
      meta: { layout: 'blank', guestOnly: true },
    },
    {
      path: '/',
      component: () => import('@/layouts/MainLayout.vue'),
      meta: { requiresAuth: true, role: 'admin' },
      children: [
        { path: '', name: 'Dashboard', component: () => import('@/pages/Dashboard.vue') },

        // Orders
        {
          path: 'orders',
          name: 'OrdersList',
          component: () => import('@/pages/orders/OrderList.vue'),
        },
        {
          path: 'orders/:id',
          name: 'OrderDetail',
          component: () => import('@/pages/orders/OrderDetail.vue'),
        },

        // Users / Engineers / Clients / Roles
        { path: 'users', name: 'UsersList', component: () => import('@/pages/users/UserList.vue') },
        {
          path: 'engineers',
          name: 'EngineersList',
          component: () => import('@/pages/engineers/EngineerList.vue'),
        },
        {
          path: 'clients',
          name: 'ClientsList',
          component: () => import('@/pages/clients/ClientList.vue'),
        },
        {
          path: 'clients/:id',
          name: 'ClientDetail',
          component: () => import('@/pages/clients/ClientDetail.vue'),
        },
        { path: 'roles', name: 'RolesList', component: () => import('@/pages/roles/RoleList.vue') },

        // Wallets
        {
          path: 'wallets/payouts',
          name: 'EngineerPayouts',
          component: () => import('@/pages/wallets/EngineerPayouts.vue'),
        },
        {
          path: 'wallets/transactions',
          name: 'WalletTransactions',
          component: () => import('@/pages/wallets/WalletTransactionsList.vue'),
        },
        {
          path: 'wallets/requests',
          name: 'WalletRequests',
          component: () => import('@/pages/wallets/WalletRequestsList.vue'),
        },

        // Job Applications
        {
          path: 'job-applications',
          name: 'JobApplicationsList',
          component: () => import('@/pages/job-applications/JobApplicationList.vue'),
        },
        {
          path: 'job-applications/:id',
          name: 'JobApplicationDetail',
          component: () => import('@/pages/job-applications/JobApplicationDetail.vue'),
        },

        // Notifications
        {
          path: 'notifications',
          name: 'Notifications',
          component: () => import('@/pages/notifications/DashboardNotificationsList.vue'),
        },
        {
          path: 'dashboard-notifications',
          name: 'DashboardNotifications',
          component: () => import('@/pages/notifications/DashboardNotificationsList.vue'),
        },

        // Settings & Profile
        {
          path: 'settings',
          name: 'Settings',
          component: () => import('@/pages/settings/SettingsForm.vue'),
        },
        {
          path: 'profile',
          name: 'AdminProfile',
          component: () => import('@/pages/profile/AdminProfile.vue'),
        },

        // Order Reviews
        {
          path: 'order-reviews',
          name: 'OrderReviewsList',
          component: () => import('@/pages/order-reviews/OrderReviewList.vue'),
        },

        // Core / Content / Catalog (CRUD entities)
        {
          path: 'cities',
          name: 'CitiesList',
          component: () => import('@/pages/cities/CityList.vue'),
        },
        {
          path: 'neighborhoods',
          name: 'NeighborhoodsList',
          component: () => import('@/pages/neighborhoods/NeighborhoodList.vue'),
        },
        {
          path: 'services',
          name: 'ServicesList',
          component: () => import('@/pages/services/ServiceList.vue'),
        },
        {
          path: 'property-types',
          name: 'PropertyTypesList',
          component: () => import('@/pages/property-types/PropertyTypeList.vue'),
        },
        {
          path: 'examination-elements',
          name: 'ExaminationElementsList',
          component: () => import('@/pages/examination-elements/ExaminationElementList.vue'),
        },
        {
          path: 'building-services',
          name: 'BuildingServicesList',
          component: () => import('@/pages/building-services/BuildingServiceList.vue'),
        },
        {
          path: 'engineer-qualifications',
          name: 'EngineerQualificationsList',
          component: () => import('@/pages/engineer-qualifications/EngineerQualificationList.vue'),
        },
        {
          path: 'contact-us',
          name: 'ContactUsList',
          component: () => import('@/pages/contact-us/ContactUsList.vue'),
        },
        {
          path: 'promo-codes',
          name: 'PromoCodesList',
          component: () => import('@/pages/promo-codes/PromoCodeList.vue'),
        },

        // Landing / About / Custom Pages
        {
          path: 'landing',
          name: 'LandingEditor',
          component: () => import('@/pages/landing/LandingEditor.vue'),
        },
        {
          path: 'about',
          name: 'AboutEditor',
          component: () => import('@/pages/landing/AboutEditor.vue'),
        },
        {
          path: 'pages',
          name: 'PageList',
          component: () => import('@/pages/landing/PageList.vue'),
        },

        // Statistics
        {
          path: 'statistics/financial',
          name: 'FinancialStatistics',
          component: () => import('@/pages/statistics/FinancialStatistics.vue'),
        },
        {
          path: 'statistics/engineers',
          name: 'EngineersStatistics',
          component: () => import('@/pages/statistics/EngineersStatistics.vue'),
        },
        {
          path: 'statistics/engineers/:id',
          name: 'EngineerStatDetail',
          component: () => import('@/pages/statistics/EngineerDetail.vue'),
        },
      ],
    },
    {
      path: '/engineer',
      component: () => import('@/layouts/MainLayout.vue'),
      meta: { requiresAuth: true, role: 'engineer' },
      children: [
        {
          path: '',
          name: 'EngineerHome',
          component: () => import('@/pages/engineer/EngineerHome.vue'),
        },
        {
          path: 'orders',
          name: 'EngineerOrdersCards',
          component: () => import('@/pages/engineer/OrdersCardsView.vue'),
        },
        {
          path: 'pool',
          name: 'EngineerOrderPool',
          component: () => import('@/pages/engineer/EngineerOrderPool.vue'),
        },
        {
          path: 'orders/:id/inspection',
          name: 'EngineerOrderInspection',
          component: () => import('@/pages/engineer/OrderInspectionForm.vue'),
        },
        {
          path: 'wallet',
          name: 'EngineerWallet',
          component: () => import('@/pages/engineer/EngineerWalletList.vue'),
        },
        {
          path: 'wallet/requests',
          name: 'EngineerWalletRequests',
          component: () => import('@/pages/engineer/EngineerWalletRequestsList.vue'),
        },
        {
          path: 'wallet/request',
          name: 'EngineerWalletRequest',
          component: () => import('@/pages/engineer/EngineerWalletRequestForm.vue'),
        },
        {
          path: 'profile',
          name: 'EngineerProfile',
          component: () => import('@/pages/engineer/EngineerProfile.vue'),
        },
        {
          path: 'reviews',
          name: 'EngineerOrderReviews',
          component: () => import('@/pages/engineer/EngineerOrderReviews.vue'),
        },
        {
          path: 'notifications',
          name: 'EngineerNotifications',
          component: () => import('@/pages/engineer/EngineerNotifications.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/pages/NotFound.vue'),
    },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'Login', query: { redirect: to.fullPath } };
  }

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: auth.user?.is_engineer ? 'EngineerHome' : 'Dashboard' };
  }

  if (to.meta.requiresAuth && to.meta.role) {
    if (typeof auth.user?.is_engineer !== 'boolean') {
      auth.logout();
      return { name: 'Login', query: { redirect: to.fullPath } };
    }
    const role = auth.user.is_engineer ? 'engineer' : 'admin';
    if (to.meta.role !== role) {
      return { name: role === 'engineer' ? 'EngineerHome' : 'Dashboard' };
    }
  }

  if (auth.isAuthenticated && auth.user?.is_engineer && to.meta.requiresAuth) {
    try {
      await auth.refreshAccreditation();
    } catch {
      auth.accreditationMissing = null;
    }
    auth.accreditationDialogVisible = auth.accreditationMissing !== false;
  }

  return true;
});

export default router;
